import { IAttrValue } from "@multiweb/types/contracts";
import { IOpenedFormInfo } from "UI/UI/UI/FormManager/IFormManagerInterfaces";
import { IOWObject } from "../../../../OW.Remoting/Remoting/DataTypes/IOWObject";

export { ExperimentName, IAppConfiguration } from "../../../../Configuration";
export { ICryptographicInfo } from "../../../../Global/Base/Crypto/ICryptographicInfo";
export { ICertificate, ISignatureInfo, SignatureInfoType } from "../../../../Global/Base/Crypto/ISignatureInfo";
export { IOrderBy, OrderType } from "../../../../Global/Base/DataSources/Interfaces";
export { CaseInsensitiveMap } from "../../../../Global/Base/DataTypes/CaseInsensitiveMap";
export { IEncAttrValue } from "../../../../Global/Base/DataTypes/IAttrValue";
export { IBigNumber } from "../../../../Global/Base/DataTypes/IBigNumber";
export { ITypeFactory } from "../../../../Global/Base/DIContainer";
export { Cursor } from "../../../../Global/Base/Enums";
export { EventCallback } from "../../../../Global/Base/Events";
export { FormatProviderRegistrator } from "../../../../Global/Base/FormatProviderRegistrator/FormatProviderRegistrator";
export { FormatProviderInputValues, FormatProviderParams } from "../../../../Global/Base/FormatProviderRegistrator/Types";
export { IDisposable } from "../../../../Global/Base/IDisposable";
export { CancelError, CancellationToken, CancellationTokenSource } from "../../../../Global/Base/Promises/CancellationTokenSource";
export { Deferred } from "../../../../Global/Base/Promises/PromiseUtils";
export { DateTimeFormatObject } from "../../../../Global/Base/Utils/DateUtils";
export { FileNameParts, FileUtils } from "../../../../Global/Base/Utils/FileUtils";
export { INumberFormatOptions } from "../../../../Global/Base/Utils/NumberUtils";
export { GetFormattedNumberStringParams } from "../../../../Global/Base/Utils/NumberUtils";
export { ConditionOperator } from "../../../../Global/Base/Utils/StringUtils";
export { CultureInfo } from "../../../../Global/Dom/CultureInfo";
export { INumberFormatInfo } from "../../../../Global/Dom/Globalization";
export { ValidationErrorType } from "../../../../OW.Remoting/Basic/EOWExceptionsInterfaces";
export { AppStorageItemType } from "../../../../OW.Remoting/Remoting/ApplicationStorage";
export { EnumDescription, IEnum, IEnumType } from "../../../../OW.Remoting/Remoting/DataTypes/IEnums";
export { IOWObject } from "../../../../OW.Remoting/Remoting/DataTypes/IOWObject";
export { IOWObjectList } from "../../../../OW.Remoting/Remoting/DataTypes/IOWObjectList";
export { IRecordIdentification } from "../../../../OW.Remoting/Remoting/DataTypes/RecordIdentification";
export { IDataEventsManager } from "../../../../OW.Remoting/Remoting/IDataEventsManager";
export { IParticipant, IWorkflowMenuItem, ParticipantType } from "../../../../OW.Remoting/Remoting/IWorkflowManager";
export { FormOpenType, RunMethodBaseParams, RunStaticMethodParams, RunStaticMethodWithParamsConfig } from "../../../../OW.Remoting/Remoting/OWManagerInterfaces";
export { RequestContext } from "../../../../OW.Remoting/Remoting/RequestContext";
export { SignatureCssClass, SignatureValidationIntermediateMsg, SignatureValidationMsg, UseCase } from "../../../../UI/UI/UI/Helpers/CryptoInfoTypes";
export { ProfileChangeType } from "../../../Shared/ProfileChangeType";
export { MenuType } from "../../../UI.Controls/Actions/MenuType";
export { IUserActionLogger } from "../../../UI/DefaultInterface/IUserActionLogger";
export { IVisible } from "../../../UI/UI/Controls/ControlInterfacesStrict";
export { IOpenedFormsHistoryManager } from "../../../UI/UI/Helpers/IOpenedFormsHistoryManager";
export { IPopupMenu } from "../../../UI/UI/IPopupMenu";
export {
    FormContext as IFormContext,
    FrameContext as IFrameContext,
    IMainMenu,
    IMainMenuFormActionData,
    IMainMenuItemActionHandler,
    IMainMenuItemActionParams,
    IMainMenuItemActionResult,
    IMainMenuNotificationGroupActionData,
    IMenuNavigationConfig,
    IWindowable,
    MainMenuItemActionData,
    MainMenuItemActionType,
    NodeDefinitionConfig,
    NodeDefinitions,
} from "../../../UI/UI/UIManagerInterfaces";
export { IDefferredWindow } from "../../../UI/Utilities/DefferredWindow";
export {
    ExtendedPropertiesType,
    MutableExtendedPropertiesType,
    MutableTagsType,
    TagsType,
} from "UI/types";
export {
    ICustomTitleChangedInfo,
    IFavoriteFormChanged,
    IFormProfileChanged,
    IHideCloseButtonChanged,
    IOpenedFormInfo,
} from "UI/UI/UI/FormManager/IFormManagerInterfaces";

// TODO Aktivovat import a smazat kopie typů, až budou oddělené (aby fungoval strict mód)
//export {IOpenedForm} from "UI/UI/UI/FormManager/IFormManager";

/**
 * Struktura s daty popisujícími otevřený formulář. Obsahuje jak konkrétní instanci formuláře, tak upřesňující data o jejím otevření.
 * @includeToDoc
 */
export interface IOpenedForm {
    /** Detailní informace týkající se otevření */
    openedFormInfo: IOpenedFormInfo;
    /** Příznak, zda se má formulář otevřít pouze na pozadí */
    openInBackground: boolean;
}

/**
 * Typ akce přidávané do menu
 * Enum je potřeba udržovat i v umístění '/MWCore/TescoSW/UI/UI.Controls/Actions/IAction.ts'
 * @includeToDoc
 */
export enum ActionType {
    none,
    custom,

    //standardní akce
    standardDeleteRecord,
    standardDeleteRecords,
    standardEditRecord,
    standardEditRecords,
    standardNewRecord,
    standardCopyRecord,
    standardSaveRecord,
    standardSaveRecordInEditContext,
    standardSaveAll,
    standardCancelChanges,
    standardCancelRecordChanges,
    standardRefreshRecord,
    standardRefreshList,
    standardOpenDetail,
    standardSelect,
    standardSelectRecord,
    standardSelectAll,
    standardDeselectAll,
    standardShowAll,
    standardShowFiltered,
    standardRunMethod,
    standardRunMethodAndClose,
    standardOpenResultForm,
    standardOpenConditionForm,
    standardCloseAndOpenConditionForm,
    standardCloseAfterWF,
    standardBack,
    standardDecrypt,
    standardRowActions,
    standardPreviousRecord,
    standardNextRecord,
    standardMoveRightRecord,
    standardMoveLeftRecord,
    standardMoveRightRecords,
    standardMoveLeftRecords,
    standardPrintAll,
    standardPrintRecord,
    standardExportCSV,
    standardExportExcel,
    standardOpenDetailOnMainForm,
    standardAuditInfoRecord,
    standardShowWFHistory,
    standardShowWFParticipants,
    standardShowWFStatus,
    standardInfoRecord,
    standardFulltextSearch,
    standardDownloadAll,
    standardGraph,
    standardColumnSettings,
    standardRecordsCount,
    standardToggleAggregationRow,
    standardShowSubRecords,

    // calendar
    standardSetToday,
    standardSwitchIntervalDay,
    standardSwitchIntervalWeek,
    standardSwitchIntervalMonth,
    //standardSwitchIntervalYear,
    standardNextInterval,
    standardPreviousInterval,

    // graf
    standardChartName,
    standardMoreSettings,
    standardChartSettings,
    standardImplementationChart,

    // filter
    standardFilter,
    standardFilterList,

    node,
    form,
    parametricForm,
    insert,
    method,
    report,
    reportAndClose,
    workflow,
    help,
    crypto,
    cryptoLabel,
}

export { TypeIdentifier } from "Global/Base/DIContainerInterfaces";

/**
 * Parametry pro otevření modálního okna
 */
export interface IOpenFormModalParams {
    /** Název formuláře k otevření */
    readonly formName: string;
    /** Seznam názvu atributů, jejichž hodnota se má získat. */
    readonly attributeNames?: readonly string[];
    /** Událost na zavření okna */
    readonly onClose?: (resultValues: readonly IAttrValue[] | undefined) => void;
    /** Podmínka pro otevíraný formulář. */
    readonly condition?: string;
    /** Instance aplikačního záznamu, ze kterého se načítají hodnoty parametrů podmínky */
    readonly conditionParamsObject?: IOWObject | null;
    /** Názvy aplikačních atributů, v nichž jsou hodnoty k parametrům podmínky */
    readonly conditionParamsAttrs?: string[];
    /** Název vazby na nadřízený objekt */
    readonly masterLinkToAttr?: string;
    /** Nadřazený object */
    readonly masterObject?: IOWObject | null;
    /** Rozlišuje mezi náhledovým a editačním režimem */
    readonly viewMode?: boolean;
    /** Jestli se jedná o LOV mód */
    readonly inLOVMode?: boolean;
    /** Jestli je v LOV módu možné provést select */
    readonly enableLOVSelect?: boolean;
}
